import React, { useState, useEffect } from 'react'
import {
    Grid, Box, Paper, Typography, Chip, Tooltip
} from "@mui/material"
import * as commonAction from "../../containers/Common/actions"
import moment from "moment"
import { FileBarChart, Mail, Calendar, Store, Copy, ClipboardCheck } from 'lucide-react'
import { toastSuccess, toastError, copyToClipboard } from '../../utils'
import CustomShowModal from '../Common/customShowModal'
import ActionButton from '../../components/ActionButton'
import EmptyState from '../../components/EmptyState'

function CopyMeta({ icon, label, value }) {
    const [copied, setCopied] = useState(false);
    if (!value || value === '-') {
        return (
            <Tooltip title={label} arrow placement="top">
                <span className="approved-release-card__meta-item">
                    {icon} -
                </span>
            </Tooltip>
        );
    }
    return (
        <Tooltip title={copied ? "Copied!" : `${label} — click to copy`} arrow placement="top">
            <span
                className="approved-release-card__meta-item approved-release-card__meta-item--copyable"
                onClick={() => { copyToClipboard(value); setCopied(true); setTimeout(() => setCopied(false), 1500); }}
            >
                {icon} {value}
                {copied ? <ClipboardCheck size={11} className="copy-tick" /> : <Copy size={11} className="copy-icon" />}
            </span>
        </Tooltip>
    );
}

export default function ReportsRequests(props) {

    const [reports, setReports] = useState([])
    const [confirmModal, setConfirmModal] = useState({
        open: false,
        reportId: null,
        action: null,
    })

    useEffect(() => {
        onGetReportRequests()
    }, [])

    const onGetReportRequests = () => {
        props.setLoader(true)
        commonAction.getReportRequestAdmin().then(res => {
            props.setLoader(false)
            const newReports = Array.isArray(res.body) ? res.body : []
            setReports([...newReports])
        }).catch(err => {
            props.setLoader(false)
            setReports([])
            toastError(err?.message || 'Failed to load report requests');
        })
    }

    const openConfirmModal = (reportId, action) => {
        setConfirmModal({ open: true, reportId, action })
    }

    const closeConfirmModal = () => {
        setConfirmModal({ open: false, reportId: null, action: null })
    }

    const onConfirmAction = () => {
        const { reportId, action } = confirmModal
        const status = action === 'deliver' ? 1 : 2
        closeConfirmModal()
        props.setLoader(true)
        commonAction.confirmReportDeliveryAdmin({ report_id: reportId, status }).then(res => {
            onGetReportRequests()
            toastSuccess(res.message)
            props.setLoader(false)
        }).catch(err => {
            props.setLoader(false)
            toastError(err?.message || 'Failed to confirm delivery')
        })
    }

    const renderReportCard = (item) => (
        <Paper
            key={item.report_id}
            className="approved-release-card"
            style={{ cursor: 'default' }}
        >
            <div className="approved-release-card__artwork">
                <div className="approved-release-card__artwork-fallback">
                    <FileBarChart size={20} color="#64748B" />
                    <span>Report</span>
                </div>
            </div>

            <div className="approved-release-card__body">
                <div className="approved-release-card__top">
                    <div className="approved-release-card__title-row">
                        <Typography className="approved-release-card__title">
                            {item.name || 'Unknown User'}
                        </Typography>
                    </div>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, flexShrink: 0, marginLeft: 'auto' }}>
                        <Calendar size={14} style={{ display: 'block', color: '#F59E0B' }} />
                        <span style={{ color: '#94A3B8', fontSize: '0.9rem', fontWeight: 500 }}>Dates:</span>
                        <span style={{ color: '#F59E0B', fontSize: '0.9rem', fontWeight: 600, fontFamily: 'monospace', letterSpacing: '0.5px' }}>
                            {moment(Number(item.start_date)).format('DD/MM/YYYY')} — {moment(Number(item.end_date)).format('DD/MM/YYYY')}
                        </span>
                    </span>
                </div>

                <div className="approved-release-card__meta">
                    <span className="approved-release-card__id">FM-RPT-{item.report_id}</span>
                    <CopyMeta icon={<Mail size={13} />} label="Email" value={item.email} />
                    <span className="approved-release-card__meta-item">
                        <Store size={13} /> {item.store || '-'}
                    </span>
                    <div style={{ display: 'flex', gap: 6, marginLeft: 'auto' }}>
                        <ActionButton variant="success" sx={{ py: 0.2, px: 1.5, fontSize: '0.75rem' }} onClick={() => openConfirmModal(item.report_id, 'deliver')}>
                            Deliver
                        </ActionButton>
                        <ActionButton variant="danger" sx={{ py: 0.2, px: 1.5, fontSize: '0.75rem' }} onClick={() => openConfirmModal(item.report_id, 'reject')}>
                            Reject
                        </ActionButton>
                    </div>
                </div>
            </div>
        </Paper>
    )

    return (
        <Grid container>
            {/* ========== Header ========== */}
            <Grid item xs={12}>
                <Box display="flex" alignItems="center" justifyContent="flex-end" gap={1.5} mb={3}>
                    <Chip
                        icon={<FileBarChart size={14} />}
                        label={`${reports.length} Pending`}
                        sx={{
                            backgroundColor: 'rgba(245, 158, 11, 0.12)',
                            color: '#F59E0B',
                            border: '1px solid rgba(245, 158, 11, 0.25)',
                            fontWeight: 600,
                            fontSize: '0.85rem',
                            height: 34,
                            flexShrink: 0,
                            '& .MuiChip-icon': { color: '#F59E0B' },
                        }}
                    />
                </Box>
            </Grid>

            {/* ========== Report Cards ========== */}
            <Grid item xs={12}>
                <Box className="approved-release-list">
                    {reports.length ? (
                        reports.map((item) => renderReportCard(item))
                    ) : (
                        <Paper sx={{ p: 0 }}>
                            <table style={{ width: '100%' }}><tbody>
                                <EmptyState message="No Report Requests Found" colSpan={1} />
                            </tbody></table>
                        </Paper>
                    )}
                </Box>
            </Grid>

            {/* ========== Confirmation Modal ========== */}
            <CustomShowModal
                handleClose={closeConfirmModal}
                title={confirmModal.action === 'deliver' ? 'Confirm Delivery' : 'Reject Report'}
                open={confirmModal.open}
                maxWidth="sm"
            >
                <Typography sx={{ color: '#94A3B8', fontSize: '0.9rem', mb: 2 }}>
                    {confirmModal.action === 'deliver'
                        ? 'Are you sure you want to mark this report as delivered? The user will be notified.'
                        : 'Are you sure you want to reject this report request? This action cannot be undone.'
                    }
                </Typography>
                <Box display="flex" justifyContent="flex-end" gap={1} py={1}>
                    <ActionButton variant="info" onClick={closeConfirmModal}>
                        Cancel
                    </ActionButton>
                    <ActionButton
                        variant={confirmModal.action === 'deliver' ? 'success' : 'danger'}
                        onClick={onConfirmAction}
                    >
                        {confirmModal.action === 'deliver' ? 'Deliver' : 'Reject'}
                    </ActionButton>
                </Box>
            </CustomShowModal>
        </Grid>
    )
}
